import React, { useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckIcon from "@mui/icons-material/Check";

// ── JSON syntax highlighter ───────────────────────────────────────────────────
function highlightJSON(json: string): string {
  return json
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
      (match) => {
        let cls = "json-number";
        if (/^"/.test(match)) {
          cls = /:$/.test(match) ? "json-key" : "json-string";
        } else if (/true|false/.test(match)) {
          cls = "json-bool";
        } else if (/null/.test(match)) {
          cls = "json-null";
        }
        return `<span class="${cls}">${match}</span>`;
      },
    );
}

interface DefinitionCodeBlockProps {
  label: string;
  code: string | any | null;
  dataTracker: string;
}

const DefinitionCodeBlock: React.FC<DefinitionCodeBlockProps> = ({
  label,
  code,
  dataTracker,
}) => {
  const [copied, setCopied] = useState(false);

  const safeCode =
    typeof code === "string"
      ? code
      : code != null
        ? JSON.stringify(code, null, 2)
        : "";

  const lines = safeCode?.split("\n");
  const highlighted = highlightJSON(safeCode);

  const handleCopy = () => {
    navigator.clipboard.writeText(safeCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([safeCode], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${label.replace(/\s+/g, "_").toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {/* ── Row: label on left, icons on right ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: "6px",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: "13px",
            color: "text.primary",
            letterSpacing: "0.01em",
          }}
        >
          {label}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
          <Tooltip title="Download JSON" placement="top">
            <IconButton
              data-tracker={dataTracker}
              size="small"
              onClick={handleDownload}
              color="secondary"
            >
              <FileDownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? "Copied!" : "Copy"} placement="top">
            <IconButton size="small" onClick={handleCopy} color="secondary">
              {copied ? (
                <CheckIcon sx={{ fontSize: 18 }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Bordered code area ── */}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "4px",
          height: "260px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "auto",
            fontFamily: "'Consolas', 'Monaco', 'Fira Code', monospace",
            fontSize: "11px",
            lineHeight: 1.65,
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-track": { bgcolor: "#f0f0f0" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#ccc",
              borderRadius: "3px",
            },
          }}
        >
          {/* Line numbers */}
          <Box
            sx={{
              minWidth: "34px",
              textAlign: "right",
              px: "6px",
              py: "8px",
              color: "#c0c0c0",
              userSelect: "none",
              flexShrink: 0,
              borderRight: "1px solid",
              borderColor: "divider",
              bgcolor: "#fafafa",
              fontFamily: "inherit",
              fontSize: "inherit",
              lineHeight: "inherit",
            }}
          >
            {lines.map((_: any, i: number) => (
              <Box key={i} sx={{ height: "1.65em" }}>
                {i + 1}
              </Box>
            ))}
          </Box>

          {/* Highlighted code */}
          <Box
            component="pre"
            sx={{
              m: 0,
              px: "10px",
              py: "8px",
              flex: 1,
              bgcolor: "#ffffff",
              overflow: "visible",
              whiteSpace: "pre",
              border: "none",
              outline: "none",
              fontFamily: "inherit",
              fontSize: "inherit",
              lineHeight: "inherit",
            }}
            dangerouslySetInnerHTML={{ __html: highlighted }}
          />
        </Box>
      </Box>

      <style>{`
        .json-key    { color: #e91e8c; }
        .json-string { color: #2aa198; }
        .json-number { color: #d19a66; }
        .json-bool   { color: #56b6c2; }
        .json-null   { color: #56b6c2; }
      `}</style>
    </Box>
  );
};

export default DefinitionCodeBlock;
